import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Analytics tracking utility
 *
 * Iter141c additions (2026-07-02) — top-of-funnel visibility:
 *   • Anonymous session ID (persists across visits via localStorage)
 *   • UTM capture from ?utm_source=&utm_campaign=&utm_medium=&utm_content=&utm_term=
 *   • Referrer + landing_url captured on first visit
 *   • Every event carries this attribution bundle so admin can trace signups
 *     back to the exact Add People ad that produced them.
 *
 * Storage strategy:
 *   • sessionStorage.analytics_session_id  → per-tab session
 *   • localStorage.pp_anon_id              → persistent anonymous visitor ID
 *   • localStorage.pp_attribution          → first-touch UTM/referrer/landing
 */

const ATTR_KEY = 'pp_attribution';
const ANON_KEY = 'pp_anon_id';

function _readJson(store, key) {
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function _writeJson(store, key, value) {
  try { store.setItem(key, JSON.stringify(value)); } catch { /* SSR / quota */ }
}

class Analytics {
  static async track(eventType, userId, eventData = {}) {
    try {
      const attribution = Analytics.getAttribution();
      await axios.post(`${BACKEND_URL}/api/analytics/track`, {
        event_type: eventType,
        user_id: userId || 'anonymous',
        event_data: {
          ...eventData,
          // Attribution bundle — carried on every event
          attribution,
        },
        session_id: this.getSessionId(),
        device_info: this.getDeviceInfo(),
        anon_id: Analytics.getAnonId(),
      }, { timeout: 4000 });
    } catch (error) {
      // Silent — analytics must never break the app
    }
  }

  // ── First-touch attribution: capture ONCE per visitor ─────────────
  static captureFirstTouchAttribution() {
    if (typeof window === 'undefined') return null;
    const existing = _readJson(window.localStorage, ATTR_KEY);
    if (existing) return existing;   // already captured; do not overwrite

    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source:   params.get('utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term:     params.get('utm_term')     || null,
      utm_content:  params.get('utm_content')  || null,
      gclid:        params.get('gclid')        || null,   // Google Ads click ID
      fbclid:       params.get('fbclid')       || null,   // Facebook click ID
      referrer:     document.referrer || null,
      landing_url:  window.location.href,
      landed_at:    new Date().toISOString(),
    };
    _writeJson(window.localStorage, ATTR_KEY, utm);
    return utm;
  }

  static getAttribution() {
    if (typeof window === 'undefined') return {};
    return _readJson(window.localStorage, ATTR_KEY) || {};
  }

  static getAnonId() {
    if (typeof window === 'undefined') return null;
    let anon = window.localStorage.getItem(ANON_KEY);
    if (!anon) {
      anon = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      try { window.localStorage.setItem(ANON_KEY, anon); } catch { /* ignore */ }
    }
    return anon;
  }

  // Specific tracking methods for common events

  static trackMealPlanGenerated(userId, planDetails) {
    this.track('meal_plan_generated', userId, {
      days: planDetails.days,
      people: planDetails.people,
      budget: planDetails.budget,
      dietary_restrictions: planDetails.dietary_restrictions || [],
      meal_count: planDetails.meal_count
    });
  }

  static trackRecipeSaved(userId, recipeDetails) {
    this.track('recipe_saved', userId, {
      recipe_id: recipeDetails.recipe_id,
      recipe_name: recipeDetails.recipe_name,
      cuisine: recipeDetails.cuisine,
      servings: recipeDetails.servings
    });
  }

  static trackShoppingListCreated(userId, listDetails) {
    this.track('shopping_list_created', userId, {
      item_count: listDetails.item_count,
      total_cost: listDetails.total_cost,
      retailers: listDetails.retailers || []
    });
  }

  static trackDietaryPreferencesSet(userId, preferences) {
    this.track('dietary_preferences_set', userId, {
      allergies: preferences.allergies || [],
      medical_conditions: preferences.medical_conditions || [],
      religious_cultural: preferences.religious_cultural || []
    });
  }

  static trackPageView(pageName, extra = {}) {
    // Note: signature CHANGED — no userId (top-of-funnel is anonymous by definition)
    this.track('page_view', null, {
      page: pageName,
      url: typeof window !== 'undefined' ? window.location.href : '',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      ...extra,
    });
  }

  static trackCtaClick(ctaName, extra = {}) {
    this.track('cta_click', null, {
      cta: ctaName,
      url: typeof window !== 'undefined' ? window.location.href : '',
      ...extra,
    });
  }

  static trackFeatureUsed(userId, featureName, details = {}) {
    this.track('feature_used', userId, {
      feature: featureName,
      ...details
    });
  }

  static trackError(userId, errorDetails) {
    this.track('error_occurred', userId, {
      error_message: errorDetails.message,
      error_type: errorDetails.type,
      page: typeof window !== 'undefined' ? window.location.href : ''
    });
  }

  // Helper methods

  static getSessionId() {
    if (typeof window === 'undefined') return null;
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  static getDeviceInfo() {
    if (typeof window === 'undefined') return {};
    return {
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language
    };
  }

  // Console logging for development visibility
  static logUserAction(action, details) {
  }
}

export default Analytics;
